/**
 * A tax period in words (FR-E16, B-10).
 *
 * A month stays `MM-YYYY`, the house spelling every screen and file already prints. A quarter of
 * a quarterly (QRMP) client is written `Q2-2026-27` on the wire, which does not say which months
 * it is, so it is written out: `Q2 2026-27 (July to September 2026)`. The same words as the
 * service's `tax_periods.period_words`, so the calendar, the rail and a reminder agree.
 */

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December',
]

const QUARTER = /^Q([1-4])-(\d{4})-(\d{2})$/
const MONTH = /^(0[1-9]|1[0-2])-\d{4}$/

/** `Q2-2026-27` — and the second year must follow the first. */
export function isQuarter(period: string): boolean {
  const m = QUARTER.exec(period)
  if (!m) return false
  return (Number(m[2]) + 1) % 100 === Number(m[3])
}

export function isMonth(period: string): boolean {
  return MONTH.test(period)
}

/**
 * What is wrong with a month typed into a box, or null when it is a month (N25). The service
 * says the same words when its own check refuses one (`tax_periods.MONTH_WORDS`); saying them
 * here, before anything is sent, is what stops `2026-07` reaching it at all.
 */
export function monthProblem(typed: string): string | null {
  const value = typed.trim()
  if (value === '') return 'Type the month the files are for, as MM-YYYY, for example 07-2026.'
  return isMonth(value) ? null : 'Write the month as MM-YYYY, for example 07-2026.'
}

/** `Q2-2026-27` → `['07-2026', '08-2026', '09-2026']`; a month is its own list. */
export function monthsOf(period: string): string[] {
  if (!isQuarter(period)) return [period]
  const m = QUARTER.exec(period) as RegExpExecArray
  const first = 3 * Number(m[1]) + 1
  const start = Number(m[2])
  return [0, 1, 2].map((offset) => {
    const month = first + offset
    const year = month <= 12 ? start : start + 1
    return `${String(((month - 1) % 12) + 1).padStart(2, '0')}-${year}`
  })
}

export function periodWords(period: string): string {
  if (!isQuarter(period)) return period
  const [firstMonth = '', , lastMonth = ''] = monthsOf(period)
  const first = MONTHS[Number(firstMonth.slice(0, 2)) - 1]
  const last = MONTHS[Number(lastMonth.slice(0, 2)) - 1]
  return `Q${period[1]} ${period.slice(3)} (${first} to ${last} ${lastMonth.slice(3)})`
}
